/**
 * Pure helpers + the optimistic write for the `studyDays` counter entity.
 *
 * Each entry stores, per local day, a `sessions` map keyed by auth session id
 * → a bag of named counters. Totals are derived by summing across sessions;
 * the merge takes per-session/per-field `max` (see `mergeStudyDay`), so these
 * writes are offline- and multi-device-safe.
 */
import type { StudyDay } from '@flashka/shared/sync-engine/schema/entities';

import { currentSession } from './session';

/**
 * Structural slice of the framework's `RecordEntityOps<StudyDay>` that
 * {@link bumpToday} needs. Declared locally instead of imported so this module
 * stays a LEAF: `sync-engine/activity/snapshot.ts` compiles it into the party
 * worker (a DOM-less tsconfig), and importing the type from
 * `create-sync-api.ts` would pull the zustand/React store chain in with it.
 * The real `sync.studyDays` ops object satisfies it structurally.
 */
type StudyDayOps = {
  get(): Record<string, StudyDay>;
  set(entry: StudyDay): void;
};

/**
 * Counter fields tracked per day. Open by design — to add a new counter, widen
 * this union and add a one-line emitter in `sync.ts`; the entity schema and the
 * merge are field-agnostic and need no change.
 *
 * Fast review has no field of its own: its good/easy reviews count as
 * `spacedRepetitionStudy` (they land in card `studyState` exactly like normal
 * SR reviews, and the server-side history backfill counts them the same way).
 *
 * `timeMs` is study time in MILLISECONDS (not a card-equivalent count). It is a
 * separate profile stat and is deliberately NOT in {@link ACTIVITY_FIELDS} — it
 * must never feed the streak/heatmap, which sum card-equivalents. Story 6
 * seeded it retro from quiz/oral/case sessions; Story 8 wired the live personal
 * flush so every study mode contributes real wall-clock ms (see
 * {@link ACTIVITY_TRACKING_LAUNCH_DATE_KEY} for the read-side handoff between the two).
 *
 * `quizStudy` (heatmap/streak) counts every ANSWERED question — correct or
 * not; `quizCorrect` (Story 8, NOT in {@link ACTIVITY_FIELDS}) counts only the
 * correct ones and drives the "Cards & questions" tile alongside
 * `spacedRepetitionStudy`. Two counters because the streak asks "did you show
 * up?" and the lifetime tile asks "how many did you get right?".
 *
 * `masteryPointsCenti` (Story 9, NOT in {@link ACTIVITY_FIELDS}) is the
 * personal Mastery-Points ledger — a SECOND, independent MP total next to the
 * per-group SGScore. Stored in integer CENTI-MP (1 MP = 100, matching
 * `study-groups/scoring.ts`) so per-event rounding never drifts; display MP is
 * `Math.round(sum / 100)` once per aggregate. It never feeds the streak/heatmap
 * (those sum card-equivalents), and — per the CTO retro rule — it is only ever
 * written live from feature launch; the server-side history backfill leaves it
 * untouched. ONE exception (designer decision): MP already earned on STUDY-GROUP
 * leaderboards is granted once into the lifetime total by the server-side
 * SG→personal import (`party/server/user/sgmp-import.ts`), written as a single
 * pre-launch day slot.
 *
 * `sessionsCompleted` (NOT in {@link ACTIVITY_FIELDS}) counts study sessions
 * FINISHED that day — a quiz, a mock exam, an oral exam or a case study that
 * reached its results. One is enough to make the day a streak day
 * ({@link dayQualifiesForStreak}); it is not a card-equivalent, so the heatmap
 * and every "cards + questions" sum leave it out.
 */
export type CounterField =
  | 'spacedRepetitionStudy'
  | 'quizStudy'
  | 'quizCorrect'
  | 'timeMs'
  | 'masteryPointsCenti'
  | 'sessionsCompleted';

/**
 * The counters that count toward the streak + heatmap: spaced-repetition cards
 * (incl. fast-review good/easy) *and* quizzes. Both the mobile and web widgets
 * read this single list so they always aggregate identically.
 */
export const ACTIVITY_FIELDS = [
  'spacedRepetitionStudy',
  'quizStudy',
] as const satisfies readonly CounterField[];

/** Daily card-equivalent target that marks a day as a completed streak day. */
export const MIN_CARDS_FOR_STREAK = 10;

/** Finished study sessions (quiz / mock exam / oral exam / case study) that
 *  mark a day as a completed streak day on their own. */
export const MIN_SESSIONS_FOR_STREAK = 1;

/** Does this day count toward a streak? See {@link dayQualifiesForStreak}. */
export type DayQualifier = (day: StudyDay | undefined) => boolean;

/**
 * THE streak-day rule (product decision 2026-10-23): a day is a streak day when
 * the user did {@link MIN_CARDS_FOR_STREAK} card-equivalents (spaced-repetition
 * reviews + ANSWERED quiz questions — the original rule, so no day that
 * qualified before stops qualifying) OR finished
 * {@link MIN_SESSIONS_FOR_STREAK} quiz, mock exam, oral exam or case study.
 *
 * Every streak reader — both Streak widgets, the profile + visitor stats, Ka's
 * store snapshot, the streak-day-2 push ask and the server-side push signals —
 * goes through this one predicate.
 */
export function dayQualifiesForStreak(day: StudyDay | undefined): boolean {
  if (!day) return false;
  return (
    dayTotal(day, ACTIVITY_FIELDS) >= MIN_CARDS_FOR_STREAK ||
    dayTotal(day, 'sessionsCompleted') >= MIN_SESSIONS_FOR_STREAK
  );
}

/** A qualifier from the legacy `(field, min)` pair, or the given predicate. */
function toQualifier(
  field: CounterField | readonly CounterField[] | DayQualifier,
  min: number,
): DayQualifier {
  if (typeof field === 'function') return field;
  return (day) => dayTotal(day, field) >= min;
}

/**
 * Estimated study time credited per spaced-repetition card review, in ms.
 * Applied to `spacedRepetitionStudy` counts on days BEFORE
 * {@link ACTIVITY_TRACKING_LAUNCH_DATE_KEY} — pre-launch history has no real
 * per-review timing (live counters didn't record it, card `studyState` is
 * trimmed), so this per-card estimate stands in for it. Post-launch days credit
 * real wall-clock ms only (see {@link ACTIVITY_TRACKING_LAUNCH_DATE_KEY}), so
 * this constant is effectively a HISTORICAL knob that will freeze the moment
 * prod launches.
 */
export const PER_CARD_STUDY_MS = 20 * 1000;

/**
 * The day live activity tracking went live — the first local day
 * (`YYYY-MM-DD`) on which the client writes real per-day counters for EVERY
 * study mode (SR/FR/quiz/oral/case). Two things hang off it:
 *
 * 1. STUDY TIME — wall-clock `timeMs` is only trustworthy from this day on, so
 *    the read-side formula is per-day:
 *
 *      day <  launch → `spacedRepetitionStudy × PER_CARD_STUDY_MS + timeMs`
 *                      (retro reconstruction: card estimate + Story-6 backfill)
 *      day >= launch → `timeMs` only (real measured — the estimate would
 *                      double-count real SR wall-clock)
 *
 *    Applied identically in `useProfileTotals` and the visitor-snapshot compute
 *    via a single shared helper so the two views can't drift.
 *
 * 2. MASTERY POINTS — `masteryPointsCenti` is never backfilled (per the CTO
 *    retro rule), so this is also the first day personal MP could be earned.
 *    The profile's Mastery-Points explainer renders this date so a power user
 *    with years of history doesn't read "MP: 0" as a bug.
 *
 * DEPLOYMENT: this must be the actual prod-deploy date of the social MVP (see
 * the social deployment checklist). Clients on older bundles simply keep
 * applying the SR estimate for a day or two, which is the correct behavior for
 * those days — but the date is user-visible in the MP explainer, so bumping it
 * also moves that copy.
 */
export const ACTIVITY_TRACKING_LAUNCH_DATE_KEY = '2026-08-11';

/**
 * Study time (ms) for ONE day, applying the {@link ACTIVITY_TRACKING_LAUNCH_DATE_KEY}
 * split. Pure — the sole source of the read-side formula, reused by
 * `useProfileTotals` AND the visitor-snapshot compute (`snapshot.ts`) so
 * the two views mechanically match.
 */
export function studyTimeMsForDay(day: StudyDay | undefined, dateKey: string): number {
  if (!day) return 0;
  const timeMs = dayTotal(day, 'timeMs');
  if (dateKey >= ACTIVITY_TRACKING_LAUNCH_DATE_KEY) return timeMs;
  return dayTotal(day, 'spacedRepetitionStudy') * PER_CARD_STUDY_MS + timeMs;
}

/**
 * Lifetime study time (ms) across every recorded day, applying the per-day
 * launch-day formula in {@link studyTimeMsForDay}. Shared by `useProfileTotals`
 * and the visitor snapshot.
 */
export function totalStudyTimeMs(days: Record<string, StudyDay>): number {
  let total = 0;
  for (const [key, day] of Object.entries(days)) total += studyTimeMsForDay(day, key);
  return total;
}

/** Local-day key `YYYY-MM-DD` (the user's calendar day, not UTC). */
export function localDayKey(d: Date = new Date()): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Local-day key `YYYY-MM-DD` from explicit calendar parts — `month` is
 * 0-indexed (matching `Date.getMonth()`), `day` is 1-indexed. The calendar-grid
 * counterpart to {@link localDayKey}; both the web and mobile heatmaps build
 * cell keys with this so they look up `byDay` identically.
 */
export function formatDateKey(year: number, month: number, day: number): string {
  const m = String(month + 1).padStart(2, '0');
  const d = String(day).padStart(2, '0');
  return `${year}-${m}-${d}`;
}

/**
 * Increment a counter for the current auth session on today's local day.
 * Optimistic local write; the engine stamps `updatedAtISO`, persists, and
 * pushes it, and the server merges by `max` so retries / offline edits /
 * concurrent devices never lose or double-count.
 */
export function bumpToday(ops: StudyDayOps, field: CounterField, amount = 1): void {
  const dateKey = localDayKey();
  const sessionId = currentSession();
  const day: StudyDay = ops.get()[dateKey] ?? {
    dateKey,
    sessions: {},
    updatedAtISO: '',
  };
  const bag = { ...(day.sessions[sessionId] ?? {}) };
  bag[field] = (bag[field] ?? 0) + amount;
  ops.set({
    dateKey,
    sessions: { ...day.sessions, [sessionId]: bag },
    updatedAtISO: new Date().toISOString(),
  });
}

// ---------------------------------------------------------------------------
// Session-completion crediting
// ---------------------------------------------------------------------------

/**
 * A once-per-attempt gate for `sessionsCompleted`: returns `true` the FIRST
 * time it sees `attemptKey` and `false` ever after, so a results screen that
 * re-renders, a completion effect that re-fires, or a second handler for the
 * same finish can never credit a session twice. A retake is a new attempt with
 * a new key and counts again. Bounded (oldest keys evicted past `limit`) — the
 * keys only need to outlive the screen that minted them.
 */
export function createSessionCompletionGate(
  limit = 500,
): (attemptKey: string) => boolean {
  const seen = new Set<string>();
  return (attemptKey) => {
    if (!attemptKey || seen.has(attemptKey)) return false;
    seen.add(attemptKey);
    if (seen.size > limit) {
      const oldest = seen.values().next().value;
      if (oldest !== undefined) seen.delete(oldest);
    }
    return true;
  };
}

/**
 * Credit one finished session to today's `sessionsCompleted` — through `gate`,
 * so the same `attemptKey` is credited at most once. Returns whether it
 * credited. The body of `sync.studyDays.completeSession`.
 */
export function completeSessionToday(
  ops: StudyDayOps,
  gate: (attemptKey: string) => boolean,
  attemptKey: string,
): boolean {
  if (!gate(attemptKey)) return false;
  bumpToday(ops, 'sessionsCompleted', 1);
  return true;
}

// ---------------------------------------------------------------------------
// Incremental quiz crediting
// ---------------------------------------------------------------------------

/**
 * Bookkeeping for crediting quiz answers to `quizStudy` AS THEY HAPPEN.
 *
 * `sessionKey` identifies the quiz attempt (its `timestampStart`); `credited`
 * is the answered-question high-water mark already bumped for that attempt.
 * `sessionKey: undefined` means nothing has been seeded yet.
 */
export interface QuizCreditMark {
  sessionKey: string | null | undefined;
  credited: number;
}

/** Nothing seeded yet. `undefined` (not `null`) so a not-yet-started attempt
 *  still takes the seeding branch on the first step. */
export const INITIAL_QUIZ_CREDIT_MARK: QuizCreditMark = {
  sessionKey: undefined,
  credited: 0,
};

/**
 * How much to credit to `quizStudy` now, given the mark from the previous step
 * and the attempt's current answered-question count.
 *
 * Two invariants make this safe to call on every render:
 *  - A CHANGED `sessionKey` (new / restarted / resumed attempt) re-SEEDS the
 *    mark from what is already answered and credits nothing. That's what stops
 *    a resumed attempt — which mounts with its earlier answers replayed into
 *    state — from re-crediting answers that were already counted on the day
 *    they were given.
 *  - Within one attempt the mark is a HIGH-WATER mark: only growth is
 *    credited, so a shrinking count (e.g. an answered question being reported
 *    and dropped from the set) can never produce a negative bump, and a
 *    re-render at the same count can never double-count.
 */
export function stepQuizCredit(
  mark: QuizCreditMark,
  sessionKey: string | null,
  answeredCount: number,
): { mark: QuizCreditMark; credit: number } {
  if (mark.sessionKey !== sessionKey) {
    return { mark: { sessionKey, credited: answeredCount }, credit: 0 };
  }
  const credit = answeredCount - mark.credited;
  if (credit <= 0) return { mark, credit: 0 };
  return { mark: { sessionKey, credited: answeredCount }, credit };
}

/** Sum one counter field (or several, e.g. cards + quizzes) across a day. */
export function dayTotal(
  day: StudyDay | undefined,
  field: CounterField | readonly CounterField[],
): number {
  if (!day) return 0;
  const fields = typeof field === 'string' ? [field] : field;
  let total = 0;
  for (const bag of Object.values(day.sessions)) {
    for (const f of fields) total += bag[f] ?? 0;
  }
  return total;
}

/**
 * Consecutive qualifying days counting back from today. By default a day
 * qualifies per {@link dayQualifiesForStreak} (the product rule); pass a
 * counter field (or several) plus `min` for a custom bar instead — note that
 * passing `ACTIVITY_FIELDS, MIN_CARDS_FOR_STREAK` explicitly gives the OLD
 * cards-only rule, not the streak. Today not yet qualifying does not break an
 * existing streak.
 */
export function computeStreak(
  days: Record<string, StudyDay>,
  field: CounterField | readonly CounterField[] | DayQualifier = dayQualifiesForStreak,
  min = MIN_CARDS_FOR_STREAK,
  asOf: Date = new Date(),
): number {
  const qualifies = toQualifier(field, min);
  let streak = 0;
  const cursor = new Date(asOf);
  const asOfKey = localDayKey(asOf);
  for (;;) {
    const key = localDayKey(cursor);
    if (qualifies(days[key])) {
      streak += 1;
    } else if (streak > 0 || key !== asOfKey) {
      break;
    }
    cursor.setDate(cursor.getDate() - 1);
    if (streak > 365) break;
  }
  return streak;
}

/** Days since the Unix epoch for a `YYYY-MM-DD` local-day key (UTC midnight —
 *  the key is already a local day, so no tz math here). */
function dayNumber(key: string): number {
  const [y, m, d] = key.split('-').map(Number);
  return Math.floor(Date.UTC(y, m - 1, d) / 86_400_000);
}

/**
 * Longest run of consecutive qualifying days anywhere in `days` (default rule:
 * {@link dayQualifiesForStreak}; a field + `min` gives a custom bar, as in
 * {@link computeStreak}). Unlike {@link computeStreak} (which counts back from
 * today), this scans the WHOLE history — the "Longest streak" profile stat.
 * Order-independent; gaps reset the run.
 */
export function computeLongestStreak(
  days: Record<string, StudyDay>,
  field: CounterField | readonly CounterField[] | DayQualifier = dayQualifiesForStreak,
  min = MIN_CARDS_FOR_STREAK,
): number {
  const qualifies = toQualifier(field, min);
  const nums = Object.keys(days)
    .filter((key) => qualifies(days[key]))
    .map(dayNumber)
    .sort((a, b) => a - b);
  let best = 0;
  let run = 0;
  let prev: number | null = null;
  for (const n of nums) {
    run = prev !== null && n === prev + 1 ? run + 1 : 1;
    if (run > best) best = run;
    prev = n;
  }
  return best;
}

/**
 * Lifetime total of `field(s)` summed across EVERY recorded day (and every
 * session slot within a day). Drives the "Cards & questions" tile (cards +
 * quizzes) and the "Study time" tile (`timeMs`). Archive-agnostic — sourced
 * from the synced counters, which include the server-side history backfill.
 */
export function totalForFields(
  days: Record<string, StudyDay>,
  field: CounterField | readonly CounterField[] = ACTIVITY_FIELDS,
): number {
  let total = 0;
  for (const day of Object.values(days)) total += dayTotal(day, field);
  return total;
}

/**
 * Percent (0–100) of `min` reached for `field(s)` on a day — the per-cell fill
 * the Streak widgets render. Both platforms share this so a day looks equally
 * full on web and mobile.
 */
export function dayFillPercent(
  day: StudyDay | undefined,
  field: CounterField | readonly CounterField[] = ACTIVITY_FIELDS,
  min = MIN_CARDS_FOR_STREAK,
): number {
  if (min <= 0) return 0;
  return Math.min(100, (dayTotal(day, field) / min) * 100);
}

/**
 * The Streak widget's per-day fill: a streak day is FULL (100) whichever way it
 * qualified — a finished quiz after three cards must not render as a 30% cell
 * next to a streak count that already includes it — otherwise the
 * card-equivalent progress toward {@link MIN_CARDS_FOR_STREAK}.
 */
export function streakDayFillPercent(day: StudyDay | undefined): number {
  return dayQualifiesForStreak(day) ? 100 : dayFillPercent(day);
}

/**
 * Map of local-day key → summed counter total for `fields`, across every
 * recorded day. This is the heatmap's data source on both platforms; sourced
 * from the counters it is archive-agnostic (cold/archived decks' card bodies
 * leave the device, but the counters never do).
 */
export function heatmapByDay(
  days: Record<string, StudyDay>,
  fields: CounterField | readonly CounterField[] = ACTIVITY_FIELDS,
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [key, day] of Object.entries(days)) {
    out[key] = dayTotal(day, fields);
  }
  return out;
}

/** Largest daily value in `byDay` over the inclusive [start, end] local-day range. */
export function maxInWindow(
  byDay: Record<string, number>,
  start: Date,
  end: Date,
): number {
  let max = 0;
  const cursor = new Date(start);
  while (cursor <= end) {
    const v = byDay[localDayKey(cursor)] ?? 0;
    if (v > max) max = v;
    cursor.setDate(cursor.getDate() + 1);
  }
  return max;
}

/**
 * Ordered design-token names for the six heatmap buckets, index-aligned with
 * {@link activityBucket} (0 = empty, 1–5 = increasing intensity). This is the
 * single source of truth for the bucket *order and set*; each platform maps
 * these names to its own format — web to a literal Tailwind class (Tailwind's
 * build scanner needs the `bg-*` strings present in source, so they can't be
 * built dynamically), app to a raw hex (React Native needs hex for a dynamic
 * `backgroundColor`). Keying the per-platform maps by these tokens turns any
 * drift in order or set into a compile error.
 */
export const ACTIVITY_BUCKET_TOKENS = [
  'secondary-100',
  'primary-100',
  'primary-200',
  'primary-300',
  'primary-400',
  'primary-500',
] as const;

export type ActivityBucketToken = (typeof ACTIVITY_BUCKET_TOKENS)[number];

/**
 * Heatmap intensity bucket `0–5` for a cell. `0` = empty; `1–5` map to the five
 * primary shades as `count / max` crosses .2/.4/.6/.8. Returning a bucket index
 * (not a colour) keeps the thresholds identical across platforms while each
 * maps the bucket to its own tokens (web = Tailwind class, app = hex).
 */
export function activityBucket(count: number, max: number): 0 | 1 | 2 | 3 | 4 | 5 {
  if (count <= 0) return 0;
  const intensity = count / Math.max(max, 1);
  if (intensity <= 0.2) return 1;
  if (intensity <= 0.4) return 2;
  if (intensity <= 0.6) return 3;
  if (intensity <= 0.8) return 4;
  return 5;
}
